import type { ExecutionRecord } from '@/services/api/retrostock-api'

// Pure progress/time math for the Run mode live strip. Kept out of the
// component file (and out of React) so it is trivial to unit test and so a
// .tsx module doesn't mix component + non-component exports (Fast Refresh).
//
// Callers pass in the already-stabilized completed/failed counts from
// useStableExecutionCounts, this module does not know about the
// DynamoDB-counter-jitter problem, it just turns numbers into a progress
// snapshot and durations into copy.

export interface RunProgressSnapshot {
  done: number
  total: number
  failed: number
  /** 0-100, clamped so it can never exceed 100 even if counters overshoot. */
  percent: number
  elapsedMs: number
  /** null once done, or when there isn't enough signal to estimate yet. */
  etaMs: number | null
}

/**
 * Snapshot of a run's progress at `now`. `stableCompleted`/`stableFailed`
 * should already be the jitter-corrected values from
 * useStableExecutionCounts, not the raw ExecutionRecord fields.
 */
export function runProgress(
  run: Pick<ExecutionRecord, 'triggeredAt' | 'totalResults'>,
  stableCompleted: number,
  stableFailed: number,
  now: number = Date.now(),
): RunProgressSnapshot {
  const total = run.totalResults ?? 0
  const done = total > 0 ? Math.min(stableCompleted, total) : stableCompleted
  const failed = stableFailed
  const percent = total > 0 ? Math.min(100, (done / total) * 100) : 0

  const triggeredMs = Date.parse(run.triggeredAt)
  const elapsedMs = Number.isFinite(triggeredMs) ? Math.max(0, now - triggeredMs) : 0

  const etaMs = done > 0 && done < total ? (total - done) * (elapsedMs / done) : null

  return { done, total, failed, percent, elapsedMs, etaMs }
}

/** "4m 12s", "58s", "1h 03m", minutes/seconds padded to 2 digits so pairs align. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  if (totalSeconds < 60) return `${totalSeconds}s`

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`
}

/** "~6m left" / "~1h left" / "under a minute left", null passes through.
 *  The tilde stands in for "about" everywhere the run surfaces show a rough
 *  time (Chris, 2026-09-06): it saves the width a column or chip lacks. */
export function formatEta(ms: number | null): string | null {
  if (ms == null) return null
  if (ms < 60_000) return 'under a minute left'

  const minutes = Math.round(ms / 60_000)
  if (minutes < 60) return `~${minutes}m left`

  const hours = Math.round(ms / 3_600_000)
  return `~${hours}h left`
}

/** "updated just now" / "updated 12 s ago" / "updated 2m ago", null passes through. */
export function formatUpdatedAgo(
  lastUpdatedAt: number | null,
  now: number = Date.now(),
): string | null {
  if (lastUpdatedAt == null) return null

  const diffMs = Math.max(0, now - lastUpdatedAt)
  if (diffMs < 3_000) return 'updated just now'

  const seconds = Math.round(diffMs / 1000)
  if (seconds < 60) return `updated ${seconds} s ago`

  const minutes = Math.round(seconds / 60)
  return `updated ${minutes}m ago`
}
